<template>
  <div
    class="media-gallery-thumbs"
    :class="`media-gallery-thumbs--${thumbsDirection}`"
  >
    <button
      v-show="scrollableThumbs && !arrivedStatePrev"
      type="button"
      class="media-gallery-thumbs__prev"
      @click="prevThumb"
    >
      <slot name="thumbs-prev-icon">
        <IconArrow class="icon icon--arrow icon--arrow-prev" />
      </slot>
    </button>

    <div
      ref="thumbs"
      class="media-gallery-thumbs__container"
    >
      <button
        v-for="item in galleryItems"
        :key="item.src"
        type="button"
        :data-thumb="item.index"
        class="media-gallery-thumbs__thumb"
        :class="{
          'media-gallery-thumbs__thumb--active': activeItem?.src === item.src,
        }"
        @click="onClick(item.index)"
        @mouseover="onMouseover(item.index)"
      >
        <UiVideo
          v-if="item.type === 'video'"
          :src="item.src"
          width="240"
          height="290"
          class="media-gallery-thumbs__video"
          :controls="false"
        />
        <img
          v-else-if="item.type === 'image'"
          :src="getImageFromProvider(item, { width: 240, height: 290, imageSize: galleryOptions?.imageSize })"
          :alt="item.alt"
          width="240"
          height="290"
          loading="lazy"
          class="media-gallery-thumbs__image"
        >
      </button>
    </div>

    <button
      v-show="scrollableThumbs && !arrivedStateNext"
      type="button"
      class="media-gallery-thumbs__next"
      @click="nextThumb"
    >
      <slot name="thumbs-next-icon">
        <IconArrow class="icon icon--arrow icon--arrow-next" />
      </slot>
    </button>
  </div>
</template>

<script lang="ts" setup>
import { useScroll } from '@vueuse/core'

const props = defineProps<{
  name: string
  thumbsDirection?: 'horizontal' | 'vertical'
  thumbsTrigger?: 'click' | 'mouseover'
}>()

const {
  galleryItems,
  galleryOptions,
  activeItem,
  setItem,
  // gallery,
  getImageFromProvider,
} = useUiMediaGallery(props.name)

const thumbs = useTemplateRef('thumbs')
const scrollableThumbs = ref(false)

const {
  x,
  y,
  arrivedState,
} = useScroll(thumbs, { behavior: 'smooth' })

function onClick(index: number) {
  if (props.thumbsTrigger === 'click') {
    setItem(index, galleryOptions.value.behavior)
  }
}

function onMouseover(index: number) {
  if (props.thumbsTrigger === 'mouseover') {
    setItem(index, galleryOptions.value.behavior)
  }
}

function prevThumb() {
  if (props.thumbsDirection === 'horizontal') {
    x.value = -(thumbs.value?.scrollWidth || 0)
  }
  else {
    y.value = -(thumbs.value?.scrollHeight || 0)
  }
}

function nextThumb() {
  if (props.thumbsDirection === 'horizontal') {
    x.value = thumbs.value?.scrollWidth || 0
  }
  else {
    y.value = thumbs.value?.scrollHeight || 0
  }
}

const arrivedStatePrev = computed(() => {
  if (props.thumbsDirection === 'horizontal') {
    return arrivedState.left
  }
  return arrivedState.top
})

const arrivedStateNext = computed(() => {
  if (props.thumbsDirection === 'horizontal') {
    return arrivedState.right
  }
  return arrivedState.bottom
})

const targetIsVisible = useElementVisibility(thumbs)

function checkScrollablity() {
  if (props.thumbsDirection === 'horizontal') {
    scrollableThumbs.value = (thumbs.value?.scrollWidth || 0) > (thumbs.value?.clientWidth || 0)
  }
  else {
    scrollableThumbs.value = (thumbs.value?.scrollHeight || 0) > (thumbs.value?.clientHeight || 0)
  }
}

watchEffect(() => {
  if (targetIsVisible.value) {
    checkScrollablity()
  }
})

onMounted(() => {
  checkScrollablity()
})
</script>

<style>
@import url("~layers/app/components/Ui/MediaGallery/Thumbs.css");
</style>
